import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { createImageVariantConfig } from '../../util/sdkLoader';
import { isTooManyRequestsError, storableError } from '../../util/errors';
import { retryOnceOn429 } from '../../util/retryOnceOn429';
import { markClientRateLimited } from '../../util/sharetribeRateLimit';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  HOMEPAGE_LISTENER_PAGE_SIZE,
  homepageQueryIsComplete,
  mergeHomepagePublishedListeners,
  resolveHomepageListingRefs,
} from '../../util/featured';
import { TALK_SESSION_LISTING_TYPE } from '../../util/sessionPricing';

export const ASSET_NAME = 'landing-page';

// Seeded listeners. Prefer these at the front of the published strip.
export const FEATURED_LISTING_IDS = [
  '6a8f123b-a19b-49d6-8df4-e5d900ffb394', // Maya Chen
  '6a8f123c-03ce-401e-b055-ae73228eedf5', // Jordan Hale
  '6a8f123d-0ce2-4a4b-8f7a-e6cda8c03b22', // Priya Shah
  '6a8f123f-fdda-4e36-9082-aa3a1c7cb175', // Alex Rivera
];

const listingImageFields = config => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  return {
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': [
      'title',
      'description',
      'price',
      'deleted',
      'state',
      'availabilityPlan',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.startTimeInterval',
      'publicData.offeredFormats',
      'publicData.priceVariationsEnabled',
      'publicData.priceVariants',
      'publicData.featured',
      'publicData.featuredUntil',
      'publicData.availableNow',
      'publicData.payoutsReady',
      'publicData.founding', 'publicData.topics', 'publicData.topicsOther', 'publicData.favoriteTeams', 'publicData.topicPaths', 'publicData.listeningStyles',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-xsmall',
      'variants.square-xsmall2x',
      'variants.square-small',
      'variants.square-small2x',
      // Default Sharetribe variants — listing-card can lag after upload and
      // would otherwise leave Featured with no usable photo URLs.
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };
};

/**
 * Paid-Featured-only query. Kept for tests and Console diagnostics. The
 * homepage no longer uses this — it hid every listener without the flag.
 *
 * @param {Object} config
 * @returns {Object}
 */
export const featuredListingsQueryParams = config => ({
  ...listingImageFields(config),
  pub_featured: true,
  perPage: 8,
});

/**
 * Id lookup must not require pub_featured. That Console filter is optional
 * and can miss a listing the owner just marked Featured.
 *
 * @param {Object} config
 * @returns {Object}
 */
export const featuredListingsByIdQueryParams = config => {
  const params = { ...featuredListingsQueryParams(config) };
  delete params.pub_featured;
  return params;
};

/**
 * Homepage Featured: every published talksession listing. No `ids` cap and
 * no `pub_featured` filter — those two are why TEST home showed one card.
 *
 * @param {Object} config
 * @returns {Object}
 */
export const homepageListenerQueryParams = config => ({
  ...listingImageFields(config),
  pub_listingType: TALK_SESSION_LISTING_TYPE,
  perPage: HOMEPAGE_LISTENER_PAGE_SIZE,
});

const fetchFeaturedListingsPayload = async (
  { config },
  { dispatch, extra: sdk, getState, rejectWithValue }
) => {
  const previousRefs = getState()?.LandingPage?.featuredListingRefs || [];
  const previousComplete = getState()?.LandingPage?.featuredComplete === true;

  const finish = (listings, sdkResponse, incomingComplete) => {
    const nextRefs = (listings || []).map(listing => listing.id);
    const { listingRefs, complete } = resolveHomepageListingRefs({
      previous: previousRefs,
      previousComplete,
      incoming: nextRefs,
      incomingComplete,
    });
    const keptPrevious = listingRefs === previousRefs && previousComplete;
    if (sdkResponse?.data?.data?.length && !keptPrevious) {
      dispatch(addMarketplaceEntities(sdkResponse));
    }
    return { listingRefs, complete };
  };

  const keepPrevious = () => ({
    listingRefs: previousRefs,
    complete: previousComplete && previousRefs.length > 0,
  });

  try {
    // One listings.query on cold home. Extra own/fill/fallback queries
    // emptied the Sharetribe bucket and 404’d the page.
    const response = await retryOnceOn429(() =>
      sdk.listings.query(homepageListenerQueryParams(config))
    );
    const raw = response?.data?.data || [];
    const listings = mergeHomepagePublishedListeners({
      own: [],
      others: raw,
      preferredIds: FEATURED_LISTING_IDS,
    });
    const incomingComplete = homepageQueryIsComplete({
      listings: raw,
      meta: response?.data?.meta,
      perPage: HOMEPAGE_LISTENER_PAGE_SIZE,
    });
    return finish(listings, response, incomingComplete);
  } catch (e) {
    if (isTooManyRequestsError(e)) {
      markClientRateLimited();
      if (previousRefs.length) {
        return keepPrevious();
      }
      return rejectWithValue(storableError(e));
    }
    if (previousRefs.length) {
      return keepPrevious();
    }
    return rejectWithValue(storableError(e));
  }
};

export const fetchFeaturedListingsThunk = createAsyncThunk(
  'LandingPage/fetchFeaturedListings',
  fetchFeaturedListingsPayload
);

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState: {
    featuredListingRefs: [],
    featuredComplete: false,
    fetchInProgress: false,
    hasFetched: false,
    fetchError: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFeaturedListingsThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchFeaturedListingsThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.hasFetched = true;
        state.featuredListingRefs = action.payload.listingRefs;
        state.featuredComplete = action.payload.complete === true;
      })
      .addCase(fetchFeaturedListingsThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.hasFetched = true;
        state.fetchError = action.payload;
        state.featuredComplete = false;
      });
  },
});

export default landingPageSlice.reducer;

export const loadData = (params, search, config) => dispatch =>
  dispatch(fetchFeaturedListingsThunk({ config }));
